import { formatPhoneNumber } from 'util/formatPhoneNumber';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { deriveLeaderUiStatus, leaderStatusLabel, OrgMemberRow, LeaderUiStatus } from './deriveLeaderUiStatus';

const TEXT_COLOR = '#383838';
const ROW_BORDER = '#DEDEDE';
const ACTIVE_COLOR = '#2E7D32';
const PENDING_COLOR = '#383838';

function MobileStatusPill ({ status }: { status: LeaderUiStatus }): ReactElement {
  const isActive = status === 'active';
  const pillColor = isActive ? ACTIVE_COLOR : PENDING_COLOR;
  const label = leaderStatusLabel(status);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: '16px',
        py: '4px',
        borderRadius: '9999px',
        border: `1px solid ${pillColor}`,
        color: pillColor,
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: '18px',
        letterSpacing: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

export function OrganizationLeadersMobileCards ({
  leaders,
  onRowPress,
}: {
  leaders: OrgMemberRow[];
  onRowPress: (leader: OrgMemberRow) => void;
}): ReactElement {
  return (
    <Box sx={{ width: '100%', pb: '24px' }}>
      {leaders.map((leader, idx) => {
        const ui = deriveLeaderUiStatus(leader);
        const phone = leader.phoneNumber && formatPhoneNumber(leader.phoneNumber);
        const isLast = idx === leaders.length - 1;

        return (
          <Box
            key={leader.id}
            component="button"
            type="button"
            onClick={() => onRowPress(leader)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              width: '100%',
              padding: '16px 0',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              textAlign: 'left',
              borderWidth: 0,
              borderTopWidth: 1,
              borderBottomWidth: isLast ? 1 : 0,
              borderStyle: 'solid',
              borderColor: ROW_BORDER,
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap="16px"
              alignItems="flex-start"
              width="100%"
            >
              {/* Top row: Name/email/phone + chevron */}
              <Box display="flex" gap="16px" alignItems="center" width="100%">
                <Box flex={1} minWidth={0}>
                  <Typography
                    sx={{
                      fontFamily: '\'Poppins\', sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: TEXT_COLOR,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {leader.firstName} {leader.lastName}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '\'Poppins\', sans-serif',
                      fontSize: '11px',
                      fontWeight: 400,
                      lineHeight: '18px',
                      color: TEXT_COLOR,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {leader.schoolEmail}
                  </Typography>
                  {phone ? (
                    <Typography
                      sx={{
                        fontFamily: '\'Poppins\', sans-serif',
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '18px',
                        color: TEXT_COLOR,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {phone}
                    </Typography>
                  ) : null}
                </Box>
                <ChevronRightIcon sx={{ fontSize: 24, color: TEXT_COLOR, flexShrink: 0 }} />
              </Box>

              {/* Middle: Role + Status pill centered */}
              <Box
                display="flex"
                flexDirection="column"
                gap="4px"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                <Typography
                  sx={{
                    fontFamily: '\'Poppins\', sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '21px',
                    color: TEXT_COLOR,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {leader.orgRole || '—'}
                </Typography>
                <MobileStatusPill status={ui} />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
